import React, { useCallback, useEffect, useRef, useState } from "react";
import moment, { Moment } from "moment-timezone";
import { useAppContextNonNull } from "@contexts/AppContext";
import { FilterValue } from "@models/IFilterPresets";
import { IFilterField, IFilterSelectOption } from "@models/IFilterConfig";
import { IOption } from "@models/IOption";
import ICategory from "@models/ICategory";
import { OwFnInput } from "../OwFnInput/OwFnInput";
import { OwMultiSelectOption } from "../OwMultiSelectOption/OwMultiSelectOption";
import OwDateInput from "../OwDateInput/OwDateInput";
import OwToggle from "../OwToggle/OwToggle";
import { TypeaheadFn } from "../TypeaheadFn/TypeaheadFn";
import OwHierarchicalCategorySelector from "../OwHierarchicalCategorySelector/OwHierarchicalCategorySelector";

type Props = {
    field: IFilterField;
    value: FilterValue;
    onChange: (key: string, value: FilterValue) => void;
    /** Enter pressed in a text/number input */
    onEnter?: () => void;
    /** Translate a label id (falls back to the raw string) */
    translate: (id: string) => string;
    /** Red accent for exclusion sections */
    isExclusion?: boolean;
    /** Label cache for typeahead values (value → label) */
    optionLabels?: { [value: string]: string };
    onOptionLabel?: (fieldKey: string, value: string, label: string) => void;
    /** Compact rendering for inline mode */
    inline?: boolean;
};

/**
 * Renders one filter field for its type. Used by both the drawer and inline mode.
 */
export const FilterField = (props: Props) => {
    const { field, value, onChange, onEnter, translate } = props;
    const inputId = `filter-field-${field.key}`;

    const onKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === "Enter" && onEnter) {
            onEnter();
        }
    };

    switch (field.type) {
        case "text":
        case "number":
            return (
                <OwFnInput
                    id={inputId}
                    name={field.key}
                    type={field.type === "number" ? "number" : "text"}
                    value={value === undefined || value === null ? "" : String(value)}
                    fn={(e: React.ChangeEvent<HTMLInputElement>) => onChange(field.key, e.target.value)}
                    onKeyDownFn={onKeyDown}
                    min={field.min}
                    max={field.max}
                    step={field.step}
                    isSmall={true}
                    className={"form-control form-control-sm"}
                />
            );

        case "select":
            return (
                <select
                    id={inputId}
                    name={field.key}
                    className={"form-select form-select-sm"}
                    value={String(value ?? "")}
                    onChange={(e) => {
                        const match = field.options?.find((o) => String(o.value) === e.target.value);
                        onChange(field.key, match ? match.value : e.target.value);
                    }}>
                    {(field.options || []).map((option) => (
                        <option key={String(option.value)} value={String(option.value)}>
                            {option.label}
                        </option>
                    ))}
                </select>
            );

        case "multiselect": {
            const options: IOption[] = (field.options || []).map((o) => ({ value: String(o.value), label: o.label }));
            const selected = Array.isArray(value) ? value : [];

            return (
                <OwMultiSelectOption
                    options={options}
                    selectedOptions={selected}
                    onChange={(next) => onChange(field.key, next)}
                    placeholder={translate("select_options")}
                    maxDisplay={props.inline ? 1 : 2}
                />
            );
        }

        case "date":
            return (
                <div className={"FilterBar-date d-flex align-items-center"}>
                    <OwDateInput
                        selectedDate={value ? String(value) : undefined}
                        fn={(date: Moment) => onChange(field.key, moment.isMoment(date) && date.isValid() ? date.format("YYYY-MM-DD") : "")}
                        className={"mb-0 me-0"}
                    />
                    {value ? (
                        <button
                            type={"button"}
                            className={"btn btn-sm btn-link text-muted px-1"}
                            title={translate("clear")}
                            aria-label={translate("clear")}
                            onClick={() => onChange(field.key, "")}>
                            <i className={"fas fa-times"} />
                        </button>
                    ) : null}
                </div>
            );

        case "toggle":
            return (
                <OwToggle
                    name={field.key}
                    label={""}
                    active={value === true}
                    fn={(e: { target: { value: boolean } }) => onChange(field.key, !!e.target.value)}
                    wrapperClassName={props.isExclusion ? "exclusion" : ""}
                />
            );

        case "typeahead":
            return (
                <TypeaheadField
                    field={field}
                    value={value}
                    onChange={onChange}
                    translate={translate}
                    optionLabels={props.optionLabels}
                    onOptionLabel={props.onOptionLabel}
                />
            );

        case "category":
            return (
                <CategoryField
                    field={field}
                    value={value}
                    onChange={onChange}
                    optionLabels={props.optionLabels}
                    onOptionLabel={props.onOptionLabel}
                />
            );

        default:
            return null;
    }
};

type CategoryFieldProps = Pick<Props, "field" | "value" | "onChange" | "optionLabels" | "onOptionLabel">;

/** "Parent > Child" path for the chip and the picker display. */
const categoryPath = (category: ICategory): string => (category.CategoryHierarchy || [category]).map((c) => c.name).join(" > ");

/**
 * Drill-down category picker: the same OwHierarchicalCategorySelector the legacy Inventory search uses.
 * Stores the category guid; the picker wants the whole category (with its hierarchy) for display, so the one
 * the user picked is kept here and a value from a preset / deep link falls back to its resolved label.
 */
const CategoryField = ({ field, value, onChange, optionLabels, onOptionLabel }: CategoryFieldProps) => {
    const { userModel, supplierModel, vendorModel } = useAppContextNonNull();
    const [picked, setPicked] = useState<ICategory | undefined>();
    const guid = value ? String(value) : "";

    let selected: ICategory | undefined;

    if (guid) {
        selected = picked && picked.guid === guid ? picked : ({ guid, name: optionLabels?.[guid] || guid } as ICategory);
    }

    return (
        <OwHierarchicalCategorySelector
            categories={field.categories || []}
            value={selected}
            onChange={(category) => {
                setPicked(category);

                if (category && onOptionLabel) {
                    onOptionLabel(field.key, category.guid, categoryPath(category));
                }

                onChange(field.key, category ? category.guid : "");
            }}
            placeholder={"select_category"}
            userModel={userModel}
            supplierModel={supplierModel}
            vendorModel={vendorModel}
        />
    );
};

type TypeaheadFieldProps = Pick<Props, "field" | "value" | "onChange" | "translate" | "optionLabels" | "onOptionLabel">;

const TypeaheadField = ({ field, value, onChange, translate, optionLabels, onOptionLabel }: TypeaheadFieldProps) => {
    const [options, setOptions] = useState<IFilterSelectOption[]>([]);
    const [resetFlag, setResetFlag] = useState(false);
    /** Sequence of the latest lookup; results (or errors) of older lookups are ignored so fast typing cannot show stale options. */
    const lookupSeq = useRef(0);

    const values: string[] = field.multiple ? (Array.isArray(value) ? value : []) : value ? [String(value)] : [];

    const lookup = useCallback(
        (text: string) => {
            const seq = ++lookupSeq.current;

            if (!field.lookup || !text.length) {
                setOptions([]);
                return;
            }

            field
                .lookup(text)
                .then((found) => {
                    if (seq === lookupSeq.current) {
                        setOptions(found);
                    }
                })
                .catch(() => {
                    if (seq === lookupSeq.current) {
                        setOptions([]);
                    }
                });
        },
        [field]
    );

    const select = (option: IFilterSelectOption) => {
        const optionValue = String(option.value);

        if (onOptionLabel) {
            onOptionLabel(field.key, optionValue, option.label);
        }

        if (field.multiple) {
            if (!values.includes(optionValue)) {
                onChange(field.key, [...values, optionValue]);
            }

            setResetFlag(true);
        } else {
            onChange(field.key, optionValue);
        }

        // A lookup still in flight must not reopen the list after a selection.
        lookupSeq.current += 1;
        setOptions([]);
    };

    const remove = (optionValue: string) => {
        if (field.multiple) {
            onChange(
                field.key,
                values.filter((v) => v !== optionValue)
            );
        } else {
            onChange(field.key, "");
            setResetFlag(true);
        }
    };

    useEffect(() => {
        if (resetFlag) {
            setResetFlag(false);
        }
    }, [resetFlag]);

    const selectedOption =
        !field.multiple && values.length ? { value: values[0], label: optionLabels?.[values[0]] || values[0] } : undefined;

    return (
        <div className={"FilterBar-typeahead"}>
            <TypeaheadFn
                lookupFn={lookup}
                availableOptions={options}
                selectedOption={selectedOption}
                selectedOptionFn={select}
                emitSelectionSetup={{ emitObject: true }}
                titleProp={"label"}
                valueProp={"value"}
                placeholder={"search"}
                inputClassName={"form-control form-control-sm"}
                isScrollable={true}
                maxHeight={240}
                resetTypeahead={resetFlag}
                resetTypeaheadCallbackFn={() => setResetFlag(false)}
                clearSelectionFn={!field.multiple && values.length ? () => remove(values[0]) : undefined}
                usePortal={true}
            />
            {field.multiple && values.length ? (
                <div className={"d-flex flex-wrap gap-1 mt-1"}>
                    {values.map((v) => {
                        const label = optionLabels?.[v] || v;

                        return (
                            <span key={v} className={"badge bg-primary rounded-pill d-inline-flex align-items-center"}>
                                {label}
                                <button
                                    type={"button"}
                                    className={"btn btn-link btn-sm p-0 ms-1 text-white lh-1"}
                                    title={translate("remove")}
                                    aria-label={`${translate("remove")} ${label}`}
                                    onClick={() => remove(v)}>
                                    <i className={"fas fa-times-circle"} />
                                </button>
                            </span>
                        );
                    })}
                </div>
            ) : null}
        </div>
    );
};
